// The positions of a deal exactly in Ange.Bot's extraction schema (docs/schnittstelle-ange-bot.md 3.2; Ange.Bot
// azure/shared/build-extraktion-request.js SCHEMA, additionalProperties false): every field present, texts instead of
// null, only the values of its enums. Ange.Bot maps nothing; what does not fit an enum becomes "" plus an entry in
// unklarheiten, so nothing is lost silently. Pure, no I/O.
import { vpeValid, type Chargenvorgabe, type Evidence, type Mengenart, type Position } from "./requirements";

export interface AngeBotAnchor {
  feld: string;
  source_id: string;
  span_text: string;
}

export interface AngeBotUnclear {
  feld: string;
  grund: string;
  source_id: string;
  span_text: string;
}

export type MengeEinheit = "kg" | "Stück" | "L" | "g" | "mg" | "";
export type SubstanzEinheit = "mg" | "µg" | "g" | "%" | "";
/** Units of the portion, the fill quantity per container (F13). */
export type PortionEinheit = "Stück" | "g" | "mg";

/** Batch size the authorisation, the customer or a process validation sets (agreed with produkt-d5, 02.11.2026). */
export type AngeBotChargenvorgabe =
  | { vorgegeben: false }
  | {
      vorgegeben: true;
      je_charge: { min: number | null; max: number | null; einheit: "kg" | "Stück" };
      chargen_je_kampagne: number | null;
      quelle: "Zulassung" | "Kundenvorgabe" | "Prozessvalidierung";
      beleg: string;
    };

export type MachbarkeitUrteil = "machbar" | "unklar" | "nicht_machbar";

/** Ange.Bot's verdict on a position, sent along only while the position is what was checked (its hash, never ours). */
export interface AngeBotMachbarkeit {
  urteil: MachbarkeitUrteil;
  begruendung: string;
  offen: string[];
  geprueft_am: string;
  eingabehash: string;
  /** From machbarkeit/3 on, unchanged as the check gave them (setting ange_bot_machbarkeit_fragen, produkt-6f). */
  kurzsatz?: string;
  fragen?: { text: string; an: "kunde" | "produktion" }[];
  vor_angebot?: { text: string; an: "kunde" | "produktion" }[];
  chargen?: string;
}

/** What the push sends beyond the base schema, each once Ange.Bot takes it (settings ange_bot_f13, ange_bot_chargenvorgabe, ange_bot_mengenart, ange_bot_vpe). */
export interface PushOptions {
  packOnly?: boolean;
  chargenvorgabe?: boolean;
  mengenart?: boolean;
  vpe?: boolean;
}

export interface AngeBotPosition {
  index: number;
  produktname: string;
  /**
   * F13 (agreed 01.11.2026 with produkt-db): nur_verpacken packs the customer's bulk, no recipe. Sent only once Ange.Bot
   * takes it (its delivery L5, setting ange_bot_f13): its gate before that refuses any field it does not know.
   */
  leistung?: "herstellen" | "nur_verpacken";
  regtyp: "nem" | "pharma" | "homoeopathikum" | "otc" | "unbekannt";
  darreichungsform: string;
  form_spec: string;
  lieferform: "bulk" | "abgefuellt" | "unbekannt";
  verpackung_roh: string;
  menge: { value: string; einheit: MengeEinheit; basis: "je_charge" | "gesamt" | "unbekannt"; anzahl_chargen: string; lieferstruktur: string };
  /** Only with nur_verpacken and required there: Ange.Bot answers either case otherwise with 422. */
  portion?: { value: string; einheit: PortionEinheit };
  substanzen: { name_raw: string; dosis: string; einheit: SubstanzEinheit; basis: "je_einheit" | "tagesdosis" | "unbekannt"; funktion: "Wirkstoff" | "Hilfsstoff" | "" }[];
  /** Only once Ange.Bot takes it; absent means "nicht gefragt". */
  chargenvorgabe?: AngeBotChargenvorgabe;
  /** Only once Ange.Bot takes it (F18) and only when known; absent means unknown, Ange.Bot's quantity plan sets it. */
  mengenart?: Mengenart;
  /** Only once Ange.Bot takes it (F20), only a whole number from 1 to 1,000,000, never for bulk; absent: unknown. */
  vpe?: number;
  /** Only once Ange.Bot takes it, and only for a position unchanged since the check. */
  machbarkeit?: AngeBotMachbarkeit;
  kundenanforderungen: never[];
  unklarheiten: AngeBotUnclear[];
  quellen_anker: AngeBotAnchor[];
}

const SOURCE: Record<Evidence["kind"], string> = { mail: "mail", attachment: "anhang", activity: "notiz" };
const sourceId = (e: Evidence | null) => (e ? `pipeline:${SOURCE[e.kind]}:${e.id}` : "");
const spanText = (e: Evidence | null) => (e?.quote ?? "").slice(0, 300);

/** Ange.Bot reads numbers the German way (a dot followed by three digits groups thousands), so a decimal comma only. */
export function germanNumber(n: number): string {
  return String(n).replace(".", ",");
}

/**
 * The number of batches only when the text is one whole number ("2", "2 Chargen"). "2–3" or "1 Probecharge, dann 4"
 * must not become 23 or 14: then null, and the push names it in unklarheiten.
 */
export function chargenAnzahl(v: string | null): string | null {
  const m = /^\s*(\d+)\s*(chargen?|batch(es)?)?\s*$/i.exec(v ?? "");
  return m ? String(Number(m[1])) : null;
}

/** Moves the decimal point exactly (in the number's text, so kg → g does not pick up binary noise). */
function shift(n: number, by: number): number {
  const [m, e = "0"] = String(n).split("e");
  return Number(`${m}e${Number(e) + by}`);
}

function decimals(n: number): number {
  const [m, e = "0"] = String(n).split("e");
  return Math.max(0, (m!.split(".")[1]?.length ?? 0) - Number(e));
}

const SMALLER: Partial<Record<MengeEinheit, MengeEinheit>> = { kg: "g", g: "mg" };

/**
 * Ange.Bot stores quantities as numeric(18,4) and never rounds (more decimals are a 422 at `menge`, "exakt, nie still
 * gerundet"): the next smaller unit of its enum makes the value exact (kg → g → mg). null: none does.
 */
export function exactMenge(m: { value: number; einheit: MengeEinheit }): { value: number; einheit: MengeEinheit } | null {
  let cur = m;
  while (decimals(cur.value) > 4) {
    const next = SMALLER[cur.einheit];
    if (!next) return null;
    cur = { value: shift(cur.value, 3), einheit: next };
  }
  return cur;
}

/** Why the quantity cannot go to Ange.Bot as it is (the deal stays in Klärung with this hint), or null. */
export function mengeProblem(amount: number | null, unit: string | null): string | null {
  if (amount === null) return null;
  const m = mengeIn(amount, unit);
  if (m ? exactMenge(m) : decimals(amount) <= 4) return null;
  return `Menge ${[germanNumber(amount), unit].filter(Boolean).join(" ")} hat mehr als 4 Nachkommastellen, auch in kleineren Einheiten; Ange.Bot rundet nicht. Bitte Wert oder Einheit klären.`;
}

export function lieferformCode(v: string | null): AngeBotPosition["lieferform"] {
  const s = (v ?? "").toLowerCase();
  if (/bulk|lose|unverpackt/.test(s)) return "bulk";
  if (/fertig|abgef(ü|ue|u)llt|verpackt|konfektioniert/.test(s)) return "abgefuellt";
  return "unbekannt";
}

/** The five product types of Ange.Bot's val_regtyp (operator, chat-494). */
export const PRODUKTARTEN = ["NEM", "Pharma", "Homöopathikum", "OTC", "Medizinprodukt"] as const;
export type Produktart = (typeof PRODUKTARTEN)[number];

/** The product type a text names, or null: unresolved, never NEM by default (an empty one is asked for). */
export function produktartOf(v: string | null): Produktart | null {
  const s = (v ?? "").toLowerCase();
  if (/hom(ö|oe|o|e)o?pathi/.test(s)) return "Homöopathikum";
  if (/medizinprodukt|medical device/.test(s)) return "Medizinprodukt";
  if (/\botc\b|freiverk|apothekenpflichtig|rezeptfrei/.test(s)) return "OTC";
  if (/arznei|pharma|verschreibungspflichtig/.test(s)) return "Pharma";
  if (/\bnem\b|nahrungserg|food supplement|dietary supplement/.test(s)) return "NEM";
  return null;
}

const REGTYP: Record<Produktart, AngeBotPosition["regtyp"]> = {
  NEM: "nem",
  Pharma: "pharma",
  Homöopathikum: "homoeopathikum",
  OTC: "otc",
  // Ange.Bot's push refuses medizinprodukt for now (its own open package, produkt-d5 02.11.2026).
  Medizinprodukt: "unbekannt",
};

export function regtypCode(v: string | null): AngeBotPosition["regtyp"] {
  const art = produktartOf(v);
  return art ? REGTYP[art] : "unbekannt";
}

/**
 * Ange.Bot stores "Aus dem Deal: " + beleg + " · Chargen je Kampagne: N" (the last part only with a number) in at
 * most 400 characters, counted as JavaScript length; longer is a 422 for the whole push (produkt-d5, 02.11.2026).
 */
export function belegMax(chargenJeKampagne: number | null): number {
  return 400 - "Aus dem Deal: ".length - (chargenJeKampagne === null ? 0 : ` · Chargen je Kampagne: ${chargenJeKampagne}`.length);
}

/** The evidence shortened to what Ange.Bot takes, cut with an ellipsis. */
function fitBeleg(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

/** The batch size as sent, or null when there is none to send (not known, or not complete: chargenvorgabeProblem). */
export function wireChargenvorgabe(c: Chargenvorgabe | undefined): AngeBotChargenvorgabe | null {
  if (!c || c.vorgegeben === null) return null;
  if (!c.vorgegeben) return { vorgegeben: false };
  if (chargenvorgabeProblem(c)) return null;
  return {
    vorgegeben: true,
    je_charge: { min: c.min, max: c.max, einheit: c.einheit! },
    chargen_je_kampagne: c.chargenJeKampagne,
    quelle: c.quelle!,
    beleg: fitBeleg(belegOf(c), belegMax(c.chargenJeKampagne)),
  };
}

/** What a human gave as evidence, else the quote of the source. */
export const belegOf = (c: Chargenvorgabe): string => (c.beleg || c.evidence?.quote || "").trim();

/**
 * Why a batch size that is set cannot go to Ange.Bot as it is, or null. Its gate refuses the whole push otherwise:
 * unit kg or Stück, a minimum or maximum, the basis, the evidence; numbers with at most 12 digits before and 3 after
 * the comma, Stück whole (produkt-d5, 02.11.2026).
 */
export function chargenvorgabeProblem(c: Chargenvorgabe): string | null {
  if (c.vorgegeben !== true) return null;
  const gaps = [
    c.min === null && c.max === null ? "Größe je Charge" : null,
    c.einheit ? null : "Einheit kg oder Stück",
    c.quelle ? null : "Grundlage (Zulassung, Kundenvorgabe oder Prozessvalidierung)",
    belegOf(c) ? null : "Beleg",
  ].filter((x) => x !== null);
  if (gaps.length) return `Chargenvorgabe unvollständig: ${gaps.join(", ")} fehlt.`;
  for (const n of [c.min, c.max]) {
    if (n === null) continue;
    if (!(n > 0)) return "Chargenvorgabe: Die Größe je Charge muss größer als 0 sein.";
    if (c.einheit === "Stück" && !Number.isInteger(n)) return `Chargenvorgabe: ${germanNumber(n)} Stück je Charge ist keine ganze Zahl.`;
    if (n >= 1e12 || decimals(n) > 3) return `Chargenvorgabe: ${germanNumber(n)} hat zu viele Stellen (Ange.Bot nimmt höchstens 12 vor und 3 nach dem Komma).`;
  }
  if (c.min !== null && c.max !== null && c.min > c.max) return "Chargenvorgabe: Das Minimum liegt über dem Maximum.";
  if (c.chargenJeKampagne !== null && !(Number.isInteger(c.chargenJeKampagne) && c.chargenJeKampagne > 0)) return "Chargenvorgabe: Chargen je Kampagne muss eine ganze Zahl sein.";
  return null;
}

/** The quantity in Ange.Bot's units; tonnes become kg. null: no unit of the enum fits. */
export function mengeIn(amount: number, unit: string | null): { value: number; einheit: MengeEinheit } | null {
  const s = (unit ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (/^(st(ü|u)ck|stk|st|pcs|pieces?|units?|kapseln?|tabletten?|softgels?|sticks?)$/.test(s)) return { value: amount, einheit: "Stück" };
  if (/^(kg|kilo|kilogramm)$/.test(s)) return { value: amount, einheit: "kg" };
  if (/^(t|tonnen?)$/.test(s)) return { value: shift(amount, 3), einheit: "kg" };
  if (/^(l|liter)$/.test(s)) return { value: amount, einheit: "L" };
  if (/^(g|gramm)$/.test(s)) return { value: amount, einheit: "g" };
  if (s === "mg") return { value: amount, einheit: "mg" };
  return null;
}

/** The quantity as sent: in Ange.Bot's units, exact where a smaller unit allows it. */
function wireMenge(amount: number, unit: string | null): { value: number; einheit: MengeEinheit } | null {
  const m = mengeIn(amount, unit);
  return m && (exactMenge(m) ?? m);
}

/**
 * The portion as sent: kg becomes g, and g with more than 3 decimals mg, because Ange.Bot reckons packaging in whole
 * milligrams (produkt-db, 01.11.2026). null: no unit fits.
 */
export function portionIn(amount: number, unit: string | null): { value: number; einheit: PortionEinheit } | null {
  const m = mengeIn(amount, unit);
  let p = m?.einheit === "kg" ? { value: shift(m.value, 3), einheit: "g" as const } : m;
  if (p?.einheit === "g" && decimals(p.value) > 3) p = { value: shift(p.value, 3), einheit: "mg" };
  return p && (p.einheit === "Stück" || p.einheit === "g" || p.einheit === "mg") ? { value: p.value, einheit: p.einheit } : null;
}

/** Decimals Ange.Bot takes for the bulk per batch when packing (whole milligrams); more is a 422 there. */
const BULK_DECIMALS: Partial<Record<MengeEinheit, number>> = { kg: 4, g: 3 };

/** Bulk unit → the portion units Ange.Bot divides it by (F13); every other pair is a 422 there. */
const PORTION_FOR: Partial<Record<MengeEinheit, readonly PortionEinheit[]>> = { kg: ["g", "mg"], g: ["g", "mg"], Stück: ["Stück"] };

/**
 * Why a position "nur verpacken" cannot go to Ange.Bot with this quantity and portion (the deal stays in Klärung with
 * the hint), or null: units that fit together, whole pieces, no rounding.
 */
export function portionProblem(
  menge: { amount: number | null; unit: string | null },
  portion: { amount: number | null; unit: string | null },
  chargen: string | null = null,
): string | null {
  if (menge.amount === null || portion.amount === null) return null;
  const text = (amount: number, unit: string | null) => [germanNumber(amount), unit].filter(Boolean).join(" ");
  const por = portionIn(portion.amount, portion.unit);
  if (!por) return `Portion ${text(portion.amount, portion.unit)}: Ange.Bot nimmt die Portion nur in Stück, g oder mg.`;
  const bulk = wireMenge(menge.amount, menge.unit);
  if (!bulk || !PORTION_FOR[bulk.einheit]?.includes(por.einheit)) {
    return `Menge in ${bulk?.einheit || menge.unit || "?"} und Portion in ${por.einheit} passen nicht zusammen: Ange.Bot teilt kg oder g durch eine Portion in g oder mg, Stück durch Stück.`;
  }
  if (por.einheit === "Stück" && !(Number.isInteger(por.value) && Number.isInteger(bulk.value))) {
    return `Menge ${text(menge.amount, menge.unit)} und Portion ${text(portion.amount, portion.unit)}: In Stück müssen beide ganze Zahlen sein; Ange.Bot teilt Stück für Stück und rundet nicht.`;
  }
  if (por.einheit === "mg" && !Number.isInteger(por.value)) {
    return `Portion ${text(portion.amount, portion.unit)} geht nicht in ganzen Milligramm auf; Ange.Bot rechnet die Verpackung in ganzen mg und rundet nicht. Bitte Wert oder Einheit klären.`;
  }
  // Ange.Bot keeps the bulk per batch: the total divided by the batches must stay exact (binary noise cut off first).
  const batches = Number(chargenAnzahl(chargen) ?? 1) || 1;
  const allowed = BULK_DECIMALS[bulk.einheit];
  if (allowed !== undefined && decimals(Number((bulk.value / batches).toFixed(9))) > allowed) {
    return `Menge ${text(menge.amount, menge.unit)}${batches > 1 ? ` auf ${batches} Chargen` : ""} geht je Charge nicht in ganzen Milligramm auf (in ${bulk.einheit} höchstens ${allowed} Nachkommastellen); Ange.Bot rundet nicht.`;
  }
  return null;
}

/** Dose units of Ange.Bot (µ is U+00B5); null when none fits (e.g. IE). */
export function substanzEinheit(unit: string | null): SubstanzEinheit | null {
  const s = (unit ?? "").trim().toLowerCase();
  if (s === "mg") return "mg";
  if (/^(µg|μg|mcg|ug)$/.test(s)) return "µg";
  if (s === "g") return "g";
  if (s === "%") return "%";
  return null;
}

const anchor = (feld: string, e: Evidence | null): AngeBotAnchor[] => (e ? [{ feld, source_id: sourceId(e), span_text: spanText(e) }] : []);

/**
 * One position for the push. Expects the required fields to be complete (readyForAngeBot); the pipeline reads doses per
 * unit, hence basis "je_einheit" (open point F5: daily doses are not recognised yet). Customer requirements are not
 * extracted yet and go as an empty list. "Nur verpacken" (F13) adds the portion; the delivery form goes as "unbekannt"
 * (Ange.Bot ignores it there), form and substances as far as known, findings and anchors as for every position.
 */
export function toAngeBotPosition(p: Position, index: number, opts: PushOptions = {}): AngeBotPosition {
  const packOnlyTaken = opts.packOnly === true;
  const v = (f: { value: string | null }) => f.value?.trim() ?? "";
  const unclear: AngeBotUnclear[] = [];
  // Before Ange.Bot takes F13 a packaging position never gets here (checkPosition blocks it), so nothing is lost.
  const packOnly = packOnlyTaken && p.leistung?.value === "nur_verpacken";
  const amount = p.menge.amount;
  // Not exact in any unit: sent unchanged, Ange.Bot's 422 shows it (readyForAngeBot holds such a deal back before).
  const menge = amount === null ? null : wireMenge(amount, p.menge.unit);
  // Likewise a portion without a fitting unit is left out and named by Ange.Bot's 422.
  const portion = packOnly && typeof p.portion?.amount === "number" ? portionIn(p.portion.amount, p.portion.unit) : null;
  if (amount !== null && !menge) {
    unclear.push({ feld: "menge.einheit", grund: `Einheit ${p.menge.unit ?? "?"} nicht im Schema`, source_id: sourceId(p.menge.evidence), span_text: spanText(p.menge.evidence) });
  }
  const chargen = v(p.chargen);
  const anzahl = chargenAnzahl(chargen);
  // Not known or not complete: left out, Ange.Bot reads that as "nicht gefragt" (checkPosition holds back a required one).
  const chargenvorgabe = opts.chargenvorgabe ? wireChargenvorgabe(p.chargenvorgabe) : null;
  // The customer's lots or deliveries (not batches of ours): Ange.Bot's model reads the sentence, its people see it as a finding.
  const lose = (p.lieferstruktur?.value ?? "").trim().slice(0, 300);
  if (lose) unclear.push({ feld: "menge.lieferstruktur", grund: lose, source_id: sourceId(p.lieferstruktur!.evidence), span_text: "" });
  if (chargen && anzahl === null) {
    unclear.push({ feld: "menge.anzahl_chargen", grund: `Chargenzahl „${chargen.slice(0, 80)}“ ist keine eindeutige Zahl`, source_id: sourceId(p.chargen.evidence), span_text: spanText(p.chargen.evidence) });
  }
  // Packaging only: the delivery form means nothing there, a finding about it would only make a needless hint.
  for (const [feld, raw, code] of [
    ["regtyp", v(p.produktart), regtypCode(v(p.produktart) || null)],
    ...(packOnly ? [] : ([["lieferform", v(p.lieferform), lieferformCode(v(p.lieferform) || null)]] as const)),
  ] as const) {
    if (!raw || code !== "unbekannt") continue;
    // A medical device is a product type of ours that Ange.Bot's push does not take yet (produkt-d5, 02.11.2026).
    const grund = feld === "regtyp" && produktartOf(raw) === "Medizinprodukt" ? "Medizinprodukt: Ange.Bot nimmt diese Produktart noch nicht als regtyp an" : `„${raw.slice(0, 80)}“ passt zu keinem Wert von Ange.Bot`;
    unclear.push({ feld, grund, source_id: "", span_text: "" });
  }
  // Packaging only: substances are information, and Ange.Bot's gate checks every one it gets, so only complete ones go.
  const subs = packOnly ? p.substanzen.filter((s) => !!s.name?.trim() && typeof s.dose === "number" && s.dose > 0 && substanzEinheit(s.unit) !== null) : p.substanzen;
  const substanzen = subs.map((s, i) => {
    const einheit = substanzEinheit(s.unit);
    if (einheit === null && s.unit) {
      unclear.push({ feld: `substanzen[${i}].einheit`, grund: `Einheit ${s.unit} nicht im Schema`, source_id: sourceId(s.evidence), span_text: spanText(s.evidence) });
    }
    return { name_raw: s.name ?? "", dosis: s.dose === null ? "" : germanNumber(s.dose), einheit: einheit ?? ("" as const), basis: "je_einheit" as const, funktion: "" as const };
  });
  return {
    index,
    produktname: p.name,
    ...(packOnlyTaken && { leistung: packOnly ? ("nur_verpacken" as const) : ("herstellen" as const) }),
    regtyp: regtypCode(v(p.produktart) || null),
    darreichungsform: v(p.darreichungsform),
    form_spec: v(p.formSpezifikation),
    lieferform: packOnly ? "unbekannt" : lieferformCode(v(p.lieferform) || null),
    verpackung_roh: v(p.verpackung),
    menge: {
      value: menge ? germanNumber(menge.value) : amount === null ? "" : germanNumber(amount),
      einheit: menge?.einheit ?? "",
      basis: "gesamt",
      anzahl_chargen: anzahl ?? "",
      lieferstruktur: lose,
    },
    ...(portion && { portion: { value: germanNumber(portion.value), einheit: portion.einheit } }),
    substanzen,
    ...(chargenvorgabe && { chargenvorgabe }),
    ...(opts.mengenart && p.mengenart?.value && { mengenart: p.mengenart.value }),
    // An invalid number would refuse the whole push at Ange.Bot: it stays away, as does a VPE of bulk goods.
    ...(opts.vpe && vpeValid(p.vpe?.value) && lieferformCode(v(p.lieferform) || null) !== "bulk" && { vpe: p.vpe!.value! }),
    kundenanforderungen: [],
    unklarheiten: unclear,
    quellen_anker: [
      ...anchor("darreichungsform", p.darreichungsform.evidence),
      ...subs.flatMap((s, i) => anchor(`substanzen[${i}]`, s.evidence)),
      ...anchor("menge", p.menge.evidence),
      // Ange.Bot keeps the batch count in the quantity sentence; the portion shows as evidence of the product (F13).
      ...anchor("menge", p.chargen.evidence),
      ...anchor("menge", p.lieferstruktur?.evidence ?? null),
      ...(packOnly ? anchor("portion", p.portion?.evidence ?? null) : anchor("lieferform", p.lieferform.evidence)),
      ...anchor("regtyp", p.produktart.evidence),
      ...anchor("form_spec", p.formSpezifikation.evidence),
      ...anchor("verpackung_roh", p.verpackung.evidence),
    ],
  };
}

/**
 * One position for Ange.Bot's feasibility check: the product form of the push without quotes from customer mails
 * (anchors and findings must be there but may be empty; the check passes everything it gets to its model).
 */
export function machbarkeitPosition(p: Position, index: number, opts: PushOptions = {}): AngeBotPosition {
  // Without quantity type and VPE: they hardly change the verdict, the lots are in lieferstruktur (produkt-d5, 02.11.2026).
  return { ...toAngeBotPosition(p, index, { ...opts, mengenart: false, vpe: false }), unklarheiten: [], quellen_anker: [] };
}

/**
 * The body of the check: the given positions numbered 0 to n−1, as Ange.Bot's gate wants it for every call;
 * `dealIndex[n]` leads its answer for index n back to the deal's position.
 */
export function machbarkeitRequest(ref: string, positions: Position[], take: (p: Position) => boolean, opts: PushOptions = {}) {
  const dealIndex = positions.flatMap((p, i) => (take(p) ? [i] : []));
  return { body: { ref, positionen: dealIndex.map((i, n) => machbarkeitPosition(positions[i]!, n, opts)) }, dealIndex };
}

/** Keys sorted at every level, undefined left out: the same content always gives the same text. */
export function canonicalJson(x: unknown): string {
  if (Array.isArray(x)) return `[${x.map(canonicalJson).join(",")}]`;
  if (x !== null && typeof x === "object") {
    const o = x as Record<string, unknown>;
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(x) ?? "null";
}

/**
 * What our fingerprint of a checked position covers: the position as the check gets it, without its place in the
 * call. Ange.Bot's eingabehash is never recomputed (its rule); ours only tells whether a verdict still fits.
 */
export function machbarkeitKey(ref: string, p: Position, opts: PushOptions = {}): string {
  const { index: _index, ...position } = machbarkeitPosition(p, 0, opts);
  return canonicalJson({ ref, position });
}
